/**
 * Created by Andrey Popov on 11/21/23.
 */

class HidingComponent extends BaseComponent {
    onCreate() {
        this.direction = HidingComponent.resolveDirection(PrefabBuilder.resolveProperty(this.options.direction, this.context));
        this.easingRate = PrefabBuilder.resolveProperty(this.options.easingRate, this.context);

        this.control = PrefabBuilder.resolveProperty(this.options.control, this.context);
        this.hiddenByDefault = PrefabBuilder.resolveProperty(this.options.hiddenByDefault, this.context);

        this.isShown = false;
    }

    onAttach() {
        const node = this.node;

        // The view a control hides is the node declared inside it.
        const target = this.target = node.children[0];

        // The node stands in for the view it hides, so it takes over where that view meant to stand - one
        // that was given a place of its own keeps it, a prefab having set it before the component got here.
        const targetAlignmentComponent = ComponentMap.detach(target, AlignmentComponent);
        if (targetAlignmentComponent && !ComponentMap.get(node, AlignmentComponent)) {
            node.setAlignment(targetAlignmentComponent.alignment);
        }

        if (!node.getLocalZOrder()) {
            node.setLocalZOrder(target.getLocalZOrder());
        }

        node.setAnchorPoint(target.getAnchorPoint());

        if (target.avoidNode) {
            node.avoidNode = target.avoidNode;
            target.avoidNode = undefined;
        }

        target.setVisible(false);
        node._hideInSceneDebugger = true;

        target.onSizeChanged = node.createListener(this.onResize.bind(this));
        target.onPositionChanged = node.createListener(this.onResize.bind(this));

        this.onResize();

        // Registering the control shows or hides it right away, which by now the node can answer for - it
        // is built and holds its target. Where it slides out to waits for the scene it hangs in.
        if (this.control) {
            cleverapps.focusManager.registerControl(this.control, node.createListener(this.toggle.bind(this)), this.hiddenByDefault);
        }
    }

    updateHiddenByDefault() {
        if (!this.control) {
            return;
        }

        const hiddenByDefault = PrefabBuilder.resolveProperty(this.options.hiddenByDefault, this.context);
        if (hiddenByDefault !== this.hiddenByDefault) {
            this.hiddenByDefault = hiddenByDefault;
            cleverapps.focusManager.setHiddenByDefault(this.control, hiddenByDefault);
        }
    }

    updateSize() {
        const contentSize = this.target.getContentSize();
        this.node.setContentSize2(contentSize.width * Math.abs(this.target.getScaleX()), contentSize.height * Math.abs(this.target.getScaleY()));
    }

    onResize() {
        this.updateSize();

        if (this.node.parent) {
            this.node.doAlignment();
        }

        this.target.setPosition(this.isShown ? this.getOriginalPosition() : this.calcHidePosition());
        this.target.setVisible(this.isShown);

        this.updateHiddenByDefault();
    }

    calcHidePosition() {
        const node = this.node;
        let direction = this.direction;

        // A node leaves through the nearest edge of the screen, which it has nothing to measure itself
        // against until it hangs in the scene - a control registered while the scene is still being put
        // together stays where it is, and the resize that follows takes it out of sight.
        if (direction === HidingComponent.DIR.NONE || direction === HidingComponent.DIR.INSTANT || !node.getParent()) {
            return this.getOriginalPosition();
        }

        const pos = node.getParent().convertToWorldSpace(node.getPosition());
        const winSize = cleverapps.resolution.getBgSize();

        if (direction === HidingComponent.DIR.AUTO) {
            const box = node.getBoundingBox();

            const deltaX = Math.max(0, Math.min(box.x, winSize.width - box.x - box.width));
            const deltaY = Math.max(0, Math.min(box.y, winSize.height - box.y - box.height));
            direction = deltaX > deltaY ? cleverapps.UI.VERTICAL : cleverapps.UI.HORIZONTAL;
        }

        const width = node.width * node.scaleX;
        const height = node.height * node.scaleY;
        const middle = cc.p(pos.x + width * (0.5 - node.anchorX), pos.y + height * (0.5 - node.anchorY));

        let hideMiddle, d;
        if (direction === cleverapps.UI.VERTICAL) {
            d = (pos.y > winSize.height / 2) ? (winSize.height - middle.y) : -middle.y;
            hideMiddle = cc.p(middle.x, middle.y + 2 * d);
        } else {
            d = (pos.x > winSize.width / 2) ? (winSize.width - middle.x) : -middle.x;
            hideMiddle = cc.p(middle.x + 2 * d, middle.y);
        }

        return node.convertToNodeSpace(cc.p(
            hideMiddle.x + width * (node.anchorX - 0.5),
            hideMiddle.y + height * (node.anchorY - 0.5)
        ));
    }

    getOriginalPosition() {
        return cc.p(this.node.width / 2, this.node.height / 2);
    }

    toggle(visible, silent) {
        if (visible) {
            this.show(silent);
        } else {
            this.hide(silent);
        }
    }

    show(silent) {
        if (this.isShown === true) {
            return;
        }

        this.isShown = true;
        this.node._hideInSceneDebugger = false;

        if (this.target.onShow) {
            this.target.onShow(silent);
        }

        cleverapps.scenes.onAvoidNodeVisibilityChanged();

        if (this.target.showAnimation) {
            this.target.showAnimation(silent);
            return;
        }

        if (this.animating && !this.animating.isDone()) {
            this.target.stopAction(this.animating);
            delete this.animating;
        }

        if (this.direction === HidingComponent.DIR.INSTANT) {
            this.target.setVisible(true);
            return;
        }

        if (this.direction === HidingComponent.DIR.NONE) {
            this.animationShow(silent);
            return;
        }

        if (silent) {
            this.target.setPosition(this.getOriginalPosition());
            this.target.setVisible(true);
            return;
        }

        this.target.setPosition(this.calcHidePosition());
        this.animating = this.target.runAction(new cc.Sequence(
            new cc.Show(),
            new cc.MoveTo(0.4, this.getOriginalPosition()).easing(cc.easeBackOut(this.easingRate))
        )).setFinalize(() => {
            this.target.setVisible(true);
            this.target.setPosition(this.getOriginalPosition());
        });
    }

    hide(silent) {
        if (this.isShown === false) {
            return;
        }

        this.isShown = false;
        this.node._hideInSceneDebugger = true;

        if (this.target.onHide) {
            this.target.onHide(silent);
        }

        cleverapps.scenes.onAvoidNodeVisibilityChanged();

        if (this.target.hideAnimation) {
            this.target.hideAnimation(silent);
            return;
        }

        if (this.animating && !this.animating.isDone()) {
            this.target.stopAction(this.animating);
            delete this.animating;
        }

        if (this.direction === HidingComponent.DIR.INSTANT) {
            this.target.setVisible(false);
            return;
        }

        if (this.direction === HidingComponent.DIR.NONE) {
            this.animationHide(silent);
            return;
        }

        if (silent) {
            this.target.setPosition(this.calcHidePosition());
            this.target.setVisible(false);
            return;
        }

        this.animating = this.target.runAction(
            new cc.Sequence(
                new cc.MoveTo(0.15, this.calcHidePosition()).easing(cc.easeBackIn(this.easingRate)),
                new cc.Hide()
            )
        );
    }

    animationShow(silent) {
        const baseScale = this.target.baseScale || 1;

        if (silent) {
            this.target.setVisible(true);
            this.target.setOpacity(255);
            this.target.setScale(baseScale);
            return;
        }

        this.animating = this.target.runAction(new cc.Sequence(
            new cc.ScaleTo(0, baseScale * 0.6),
            new cc.FadeTo(0, 0),
            new cc.Show(),
            new cc.Spawn(
                new cc.ScaleTo(0.15, baseScale).easing(cc.easeBackOut(this.easingRate)),
                new cc.FadeIn(0.15)
            )
        )).setFinalize(() => {
            this.target.setVisible(true);
            this.target.setOpacity(255);
            this.target.setScale(baseScale);
        });
    }

    animationHide(silent) {
        const baseScale = this.target.baseScale || 1;

        if (silent) {
            this.target.setScale(baseScale);
            this.target.setOpacity(255);
            this.target.setVisible(false);
            return;
        }

        this.animating = this.target.runAction(new cc.Spawn(
            new cc.ScaleTo(0.15, 0.6).easing(cc.easeBackIn(this.easingRate)),
            new cc.FadeOut(0.15)
        )).setFinalize(() => {
            this.target.setScale(baseScale);
            this.target.setOpacity(255);
            this.target.setVisible(false);
        });
    }
}

// A control that stands among the nodes a scene lays out around answers for the view it hides - anything
// else is always there.
HidingComponent.isNodeShown = function (node) {
    const component = ComponentMap.get(node, HidingComponent);
    return !component || component.isShown;
};

// A prefab names a direction, js passes one.
HidingComponent.resolveDirection = function (direction) {
    if (typeof direction === "string") {
        if (direction === "VERTICAL") {
            return cleverapps.UI.VERTICAL;
        }
        if (direction === "HORIZONTAL") {
            return cleverapps.UI.HORIZONTAL;
        }
        return HidingComponent.DIR[direction];
    }

    return direction !== undefined ? direction : HidingComponent.DIR.AUTO;
};

HidingComponent.DIR = {
    AUTO: 2,
    NONE: 3,
    INSTANT: 4
};

Prefab.registerComponent("HidingComponent", HidingComponent);
